/**
 * client/input/input.ts — the BO2 InputSource: keyboard/mouse + DualSense (Gamepad API), aim assist,
 * stance/ADS/sprint/use timing, rumble. DOM glue only; the logic lives in pure helper files:
 *   stick.ts (deadzones, virtual axes, move scale), look.ts (curve, turn rates, accel), layouts.ts,
 *   pad.ts (device pick + raw mapping), clinput.ts (kbutton/stance/ADS port), aimassist.ts, rumble.ts,
 *   dualsense-hid.ts (WebHID).
 *
 * OWNER: INPUT agent. Sources: research/controller.md (CTL) §1-§8, research/netcode.md §7.2 (look applied
 * every render frame, 60 Hz cmds, latched edges) and §12.17 (aim assist uses interpolated players).
 *
 * Frame flow (ARCHITECTURE §4): session calls frame(ctx) once per rAF (look deltas), then cmdIntent(ctx)
 * 0..4 times for the due 60 Hz cmd slots (buttons/moves/weapon/melee lunge; consumes latched taps).
 */
import { CG_FOV, CG_FOV_VERTICAL } from '@shared/constants';
import { angleToShort, type Vec3 } from '@shared/math';
import { Button, GameEventType, PEv, WeaponId, type PlayerState } from '@shared/types';
import { getWeaponDef } from '@shared/weapons/defs';
import { CONTENTS, createTraceResult, type CollisionWorld } from '@shared/collision/types';
import type { CmdIntent, ClientWorld, GamepadStatus, InputContext, InputFrame, InputSource } from '../types';
import type { KbAction, Settings } from '../settings/settings';
import { AimAssist, aimPlayerFromPs, type AimCandidate, type AimPlayer, type AimView } from './aimassist';
import { ClStance, ClientInputState, KB, isSprinting, type StanceView } from './clinput';
import { DualSenseHid, webHidAvailable } from './dualsense-hid';
import { PAD_BUTTON_COUNT, Pad, resolveButtonLayout, type EngineCmd } from './layouts';
import { fovTurnRateScale, sensitivityForSlot, vfovFromCodFov } from './look';
import { createPadRawState, createPadSample, padHasInput, pickPad, readPad, type GamepadLike, type PadRawState, type PadSample } from './pad';
import { RumbleMixer } from './rumble';
import { clampChar, conditionStick, createVirtualAxes, gamepadMove, mapVirtualAxes, triggerAnalog } from './stick';

/** AimTarget_IsTargetVisible trace mask 8400899 = SOLID | FOLIAGE | CLIPSHOT | 0x800000 (T5). Glass is see-through. */
const AIM_VIS_MASK = CONTENTS.SOLID | CONTENTS.FOLIAGE | CONTENTS.CLIPSHOT;
/** Rumble mixer rate (~30 Hz) and per-call effect length [CTL §8.1]. */
const RUMBLE_INTERVAL_MS = 33;
const RUMBLE_EFFECT_MS = 50;
/** Sticky weapon request gives up after this long (ESTIMATE; T5 keeps cmd.weapon forever). */
const WEAPON_REQUEST_TIMEOUT_MS = 3000;
const PRIMARIES: readonly WeaponId[] = [WeaponId.CROSSBOW, WeaponId.KNIFE_BALLISTIC];

type HapticActuatorLike = {
  effects?: readonly string[];
  playEffect?: (type: string, params: { duration: number; startDelay: number; strongMagnitude: number; weakMagnitude: number }) => Promise<unknown>;
  reset?: () => Promise<unknown>;
};

export class BO2Input implements InputSource {
  // ---- state ---------------------------------------------------------------------------------
  readonly cl = new ClientInputState();
  readonly aim = new AimAssist();
  readonly mixer = new RumbleMixer();
  readonly hid = new DualSenseHid();
  private canvas: HTMLCanvasElement | null = null;
  private world: CollisionWorld | null = null;
  private readonly trace = createTraceResult();
  private readonly zero: Vec3 = [0, 0, 0];
  private settings: Settings | null = null;
  // mouse
  private mdx = 0;
  private mdy = 0;
  private wasLocked = false;
  private lockRequested = false;
  // pad
  private padIndex = -1;
  private readonly pad: PadSample = createPadSample();
  private readonly padProbe: PadSample = createPadSample();
  private readonly padRaw = new Map<number, PadRawState>();
  private readonly padPrev: number[] = new Array(PAD_BUTTON_COUNT).fill(0);
  private lastPollAt = -1e9;
  private padConnected = false;
  private layoutKey = '';
  private layout: readonly (EngineCmd | null)[] = resolveButtonLayout('default', false);
  private readonly L: [number, number] = [0, 0];
  private readonly R: [number, number] = [0, 0];
  private readonly va = createVirtualAxes();
  private readonly mv: [number, number] = [0, 0];
  private readonly km: [number, number] = [0, 0];
  private padForward = 0;
  private padSide = 0;
  private device: 'kbm' | 'gamepad' = 'kbm';
  // meta
  private pauseLatched = false;
  /** A menu is open (InputContext.suspended): devices only drive the menu, nothing reaches the game. */
  private suspended = false;
  private lastSpawnCount = -1;
  private lastPmType = -1;
  private prevMelee = false;
  private wantWeapon: WeaponId = WeaponId.NONE;
  private wantSince = 0;
  private readonly melee = { yaw: 0, dist: 0 };
  private readonly look: [number, number] = [0, 0];
  private readonly candidates: { clientNum: number; origin: AimCandidate['origin']; velocity: AimCandidate['velocity']; viewHeight: number; alive: boolean }[] = [];
  private readonly aimPlayer = {} as AimPlayer;
  private readonly aimView: AimView = { eye: [0, 0, 0], angles: [0, 0, 0], fovY: CG_FOV_VERTICAL, screenW: 1280, screenH: 720, cgFov: CG_FOV };
  private readonly eye: Vec3 = [0, 0, 0];
  // rumble output
  private lastRumbleSend = 0;
  private rumbleWasOn = false;
  private readonly motors = { strong: 0, weak: 0 };
  private hidTried = false;
  /** Debug/test handle: last values (exposed on window.__input). */
  readonly debug = { yawRate: 0, pitchRate: 0, slowdown: false, lockOn: -1, autoMelee: 0, targets: 0, fovY: CG_FOV_VERTICAL, device: 'kbm', lastButtons: 0 };

  // ---- InputSource ----------------------------------------------------------------------------
  get pointerLocked(): boolean {
    return !!this.canvas && document.pointerLockElement === this.canvas;
  }

  get gamepad(): GamepadStatus {
    const supported = typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function';
    // menus run without frame(): refresh the connection state from the Gamepad API directly
    if (supported && performance.now() - this.lastPollAt > 250) {
      const list = navigator.getGamepads() as readonly (GamepadLike | null)[];
      const idx = pickPad(list, this.padIndex, () => false);
      const gp = idx >= 0 ? list.find((g) => g && g.index === idx) ?? null : null;
      this.padConnected = !!gp;
      if (gp) readPad(gp, this.pad, this.rawState(gp.index));
    }
    const act = this.activeActuator();
    return {
      supported,
      connected: this.padConnected,
      needsGesture: supported && !this.padConnected,
      active: this.device === 'gamepad',
      id: this.pad.id,
      isDualSense: this.pad.isDualSense,
      mapping: this.padConnected ? this.pad.mapping : '',
      rumble: this.hid.connected ? 'webhid' : act ? 'gamepad' : 'none',
      webHidAvailable: webHidAvailable(),
    };
  }

  setCollisionWorld(world: CollisionWorld | null): void {
    this.world = world;
  }

  async requestHidRumble(): Promise<boolean> {
    const ok = await this.hid.request();
    if (ok && this.settings) this.settings.webHidRumble = true;
    return ok;
  }

  attach(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('wheel', this.onWheel, { passive: true });
    canvas.addEventListener('contextmenu', this.onContext);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('pointerlockchange', this.onLockChange);
    document.addEventListener('pointerlockerror', this.onLockError);
    window.addEventListener('gamepadconnected', this.onPadConnected);
    window.addEventListener('gamepaddisconnected', this.onPadDisconnected);
    (window as unknown as { __input?: BO2Input }).__input = this;
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas?.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    this.canvas?.removeEventListener('wheel', this.onWheel);
    this.canvas?.removeEventListener('contextmenu', this.onContext);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('pointerlockerror', this.onLockError);
    window.removeEventListener('gamepadconnected', this.onPadConnected);
    window.removeEventListener('gamepaddisconnected', this.onPadDisconnected);
    this.stopRumble();
    void this.hid.close();
    this.canvas = null;
  }

  rumble(name: string, scale = 1): void {
    this.mixer.play(name, performance.now(), scale);
  }

  // ---- DOM events -----------------------------------------------------------------------------
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (isTypingTarget(e.target)) return;
    if (e.code === 'Tab' || (e.code === 'Space' && this.pointerLocked)) e.preventDefault();
    if (e.repeat) return;
    this.device = 'kbm';
    this.bindKey(e.code, true, e.timeStamp || performance.now());
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.bindKey(e.code, false, e.timeStamp || performance.now());
  };
  private readonly onMouseDown = (e: MouseEvent): void => {
    if (this.canvas && document.pointerLockElement !== this.canvas) { this.requestLock(); return; }
    this.device = 'kbm';
    this.bindKey(`Mouse${e.button}`, true, performance.now());
  };
  private readonly onMouseUp = (e: MouseEvent): void => {
    this.bindKey(`Mouse${e.button}`, false, performance.now());
  };
  private readonly onMouseMove = (e: MouseEvent): void => {
    if (this.canvas && document.pointerLockElement === this.canvas) {
      this.mdx += e.movementX; this.mdy += e.movementY;
      if (e.movementX || e.movementY) this.device = 'kbm';
    }
  };
  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.pointerLocked || !e.deltaY) return;
    const code = e.deltaY < 0 ? 'WheelUp' : 'WheelDown';
    const now = performance.now();
    this.bindKey(code, true, now); // a wheel notch is a press + release: the wasPressed latch carries it
    this.bindKey(code, false, now);
  };
  private readonly onContext = (e: Event): void => e.preventDefault();
  private readonly onBlur = (): void => { this.cl.clearKeys(); this.padPrev.fill(0); };
  private readonly onLockChange = (): void => {
    const locked = this.pointerLocked;
    if (this.wasLocked && !locked) {
      // Esc released the pointer (the key event itself is swallowed by the browser): open the pause menu.
      this.pauseLatched = true;
      this.cl.clearKeys();
    }
    this.wasLocked = locked;
    this.lockRequested = false;
  };
  private readonly onLockError = (): void => { this.lockRequested = false; };
  private readonly onPadConnected = (): void => { this.lastPollAt = -1e9; };
  private readonly onPadDisconnected = (e: GamepadEvent): void => {
    if (e.gamepad.index === this.padIndex) {
      this.cl.releaseSources(`pad${this.padIndex}:`);
      this.padIndex = -1;
      this.padPrev.fill(0);
    }
    this.padRaw.delete(e.gamepad.index);
  };

  private requestLock(): void {
    const c = this.canvas;
    if (!c || this.lockRequested) return;
    this.lockRequested = true;
    const raw = this.settings?.mouseRawInput ?? true;
    try {
      // raw movementX/Y without OS acceleration where supported (Chrome); fall back to a plain lock
      const p = (c.requestPointerLock as (o?: { unadjustedMovement?: boolean }) => Promise<void> | void).call(c, raw ? { unadjustedMovement: true } : undefined);
      if (p && typeof (p as Promise<void>).catch === 'function') {
        (p as Promise<void>).catch(() => {
          try { const q = c.requestPointerLock() as unknown as Promise<void> | undefined; q?.catch?.(() => { this.lockRequested = false; }); } catch { this.lockRequested = false; }
        });
      }
    } catch {
      this.lockRequested = false;
    }
  }

  // ---- keyboard / mouse bindings (settings.keybinds) -----------------------------------------
  private bindKey(code: string, down: boolean, now: number): void {
    const s = this.settings;
    if (!s) return;
    // menu open: key presses belong to the menu (releases still pass so nothing sticks)
    if (down && this.suspended) return;
    const src = `key:${code}`;
    for (const a of Object.keys(s.keybinds) as KbAction[]) {
      if (!s.keybinds[a].includes(code)) continue;
      this.kbAction(a, down, src, now, s);
    }
    // a key rebound while held must not stay stuck
    if (!down) this.cl.releaseSources(src);
  }

  private kbAction(a: KbAction, down: boolean, src: string, now: number, s: Settings): void {
    const cl = this.cl;
    const sv = this.stanceView();
    const key = (kb: KB) => (down ? cl.keyDown(kb, src, now) : cl.keyUp(kb, src));
    switch (a) {
      case 'forward': key(KB.FORWARD); break;
      case 'back': key(KB.BACK); break;
      case 'left': key(KB.MOVELEFT); break;
      case 'right': key(KB.MOVERIGHT); break;
      case 'jump': if (down) cl.goStandDown(src, now); else cl.goStandUp(src); break; // BO2 PC: Space = +gostand
      case 'crouch':
        if (s.toggleCrouch) { if (down) cl.toggleStance(ClStance.CROUCH, sv); } // togglecrouch
        else key(KB.DOWN); // +movedown (hold)
        break;
      case 'prone':
        if (s.toggleCrouch) { if (down) cl.toggleStance(ClStance.PRONE, sv); } // toggleprone (dive when sprinting)
        else key(KB.PRONE); // +prone (hold)
        break;
      case 'sprint': key(KB.BREATH); key(KB.SPRINT); break; // +breath_sprint
      case 'attack': key(KB.ATTACK); break;
      case 'ads':
        if (s.toggleAds) { if (down) { cl.keyDown(KB.THROW, src, now); cl.toggleAdsValue(1); } else { cl.keyUp(KB.THROW, src); cl.toggleAdsValue(0); } }
        else if (down) cl.speedDown(src, now); else cl.speedUp(src);
        break;
      case 'melee': key(KB.MELEE); break;
      case 'reload': key(KB.RELOAD); break;
      case 'use': key(KB.ACTIVATE); break;
      case 'lethal': key(KB.FRAG); break;
      case 'tactical': key(KB.SMOKE); break;
      case 'switchWeapon': if (key(KB.WEAPNEXT) && down) this.requestNextWeapon(now); break;
      case 'weapon1': if (down) this.requestWeapon(WeaponId.CROSSBOW, now); break;
      case 'weapon2': if (down) this.requestWeapon(WeaponId.KNIFE_BALLISTIC, now); break;
      case 'scoreboard': key(KB.SCORES); break;
      case 'pause': if (down) this.pauseLatched = true; break;
    }
  }

  // ---- gamepad commands (layout) --------------------------------------------------------------
  private padCommand(cmd: EngineCmd, down: boolean, val: number, src: string, now: number): void {
    const cl = this.cl;
    const key = (kb: KB) => (down ? cl.keyDown(kb, src, now, val) : cl.keyUp(kb, src));
    switch (cmd) {
      case 'attack': key(KB.ATTACK); break;
      case 'speed_throw': if (down) cl.speedDown(src, now, val); else cl.speedUp(src); break;
      case 'toggleads_throw': key(KB.THROW); cl.toggleAdsValue(down ? val : 0); break;
      case 'frag': key(KB.FRAG); break;
      case 'smoke': key(KB.SMOKE); break;
      case 'melee': key(KB.MELEE); break;
      case 'breath_sprint': key(KB.BREATH); key(KB.SPRINT); break;
      case 'gostand': if (down) cl.goStandDown(src, now); else cl.goStandUp(src); break;
      case 'stance': if (down) cl.stanceDown(now, this.stanceView()); else cl.stanceUp(this.stanceView()); break;
      case 'usereload': key(KB.USERELOAD); break;
      case 'weapnext_inventory': if (key(KB.WEAPNEXT) && down) this.requestNextWeapon(now); break;
      case 'togglescores': key(KB.SCORES); break;
      case 'togglemenu': if (down) this.pauseLatched = true; break;
      default: break; // actionslots: no killstreaks in wager matches [CTL §2.1]
    }
  }

  /** Poll the Gamepad API once per frame; turn button transitions into layout commands. */
  private pollPad(now: number, s: Settings): void {
    this.lastPollAt = now;
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const list = nav && typeof nav.getGamepads === 'function' ? (nav.getGamepads() as readonly (GamepadLike | null)[]) : [];
    const idx = pickPad(list, this.padIndex, (gp) => padHasInput(readPad(gp, this.padProbe, this.rawState(gp.index))));
    if (idx !== this.padIndex) {
      if (this.padIndex >= 0) this.cl.releaseSources(`pad${this.padIndex}:`);
      this.padIndex = idx;
      this.padPrev.fill(0);
    }
    const gp = idx >= 0 ? list.find((g) => g && g.index === idx) ?? null : null;
    this.padConnected = !!gp;
    if (!gp) { this.padForward = 0; this.padSide = 0; this.va.side = this.va.forward = this.va.yaw = this.va.pitch = 0; return; }
    readPad(gp, this.pad, this.rawState(gp.index));
    // layout (rebuilt when settings change)
    const lk = `${s.buttonLayout}/${s.triggersFlipped}`;
    if (lk !== this.layoutKey) {
      this.cl.releaseSources(`pad${idx}:`);
      this.padPrev.fill(0);
      this.layout = resolveButtonLayout(s.buttonLayout, s.triggersFlipped);
      this.layoutKey = lk;
    }
    let any = false;
    for (let i = 0; i < PAD_BUTTON_COUNT; i++) {
      const raw = this.pad.buttons[i];
      // triggers: gpad_button_deadzone 0.13 analog; digital buttons: pressed > 0.5
      const val = i === Pad.L2 || i === Pad.R2 ? triggerAnalog(raw) : raw > 0.5 ? 1 : 0;
      const prev = this.padPrev[i];
      this.padPrev[i] = val;
      const cmd = this.layout[i];
      const src = `pad${idx}:${i}`;
      if (val > 0) any = true;
      if (!cmd) continue;
      // menu open: the pad drives the menu only. padPrev still tracks the buttons, so a button pressed in the
      // menu (✕ on RESUME GAME, ○ Back, R2) and still held after it closes produces no press edge.
      if (this.suspended) continue;
      if (val > 0 && prev <= 0) this.padCommand(cmd, true, val, src, now);
      else if (val <= 0 && prev > 0) this.padCommand(cmd, false, 0, src, now);
      else if (val > 0 && val !== prev) {
        // analog value change while held (IN_ToggleADS re-evaluates; kbutton val)
        if (cmd === 'toggleads_throw') this.cl.toggleAdsValue(val);
        else if (cmd === 'attack') this.cl.keyValue(KB.ATTACK, val);
      }
    }
    // sticks: radial deadzone per stick, browser +y down -> flip so up = + [CTL §8.2]
    conditionStick(this.L, this.pad.lx, -this.pad.ly);
    conditionStick(this.R, this.pad.rx, -this.pad.ry);
    mapVirtualAxes(this.va, this.L, this.R, s.stickLayout);
    if (this.L[0] || this.L[1] || this.R[0] || this.R[1]) any = true;
    if (any) this.device = 'gamepad';
    gamepadMove(this.mv, this.va.forward, this.va.side);
    this.padForward = this.mv[0];
    this.padSide = this.mv[1];
  }

  private rawState(index: number): PadRawState {
    let r = this.padRaw.get(index);
    if (!r) { r = createPadRawState(); this.padRaw.set(index, r); }
    return r;
  }

  // ---- helpers ---------------------------------------------------------------------------------
  private psView: PlayerState | null = null;
  private readonly sv: StanceView = { sprinting: false };
  private stanceView(): StanceView {
    const ps = this.psView;
    this.sv.sprinting = ps ? isSprinting(ps) : false;
    this.sv.pmFlags = ps ? ps.pm_flags : undefined; // sprint click latch (clinput.ts sprintLatched)
    this.sv.pmTime = ps ? ps.pm_time : undefined;
    return this.sv;
  }

  private requestNextWeapon(now: number): void {
    const ps = this.psView;
    if (!ps) return;
    const owned = PRIMARIES.filter((w) => (ps.weaponsOwned & (1 << w)) !== 0);
    if (owned.length < 2) return;
    const cur = this.wantWeapon !== WeaponId.NONE ? this.wantWeapon : ps.weaponPending !== WeaponId.NONE ? ps.weaponPending : ps.weapon;
    const i = owned.indexOf(cur);
    this.wantWeapon = owned[(i + 1) % owned.length];
    this.wantSince = now;
  }
  private requestWeapon(w: WeaponId, now: number): void {
    const ps = this.psView;
    if (!ps || (ps.weaponsOwned & (1 << w)) === 0) return;
    if (ps.weapon === w && ps.weaponPending === WeaponId.NONE) { this.wantWeapon = WeaponId.NONE; return; }
    this.wantWeapon = w;
    this.wantSince = now;
  }
  /** Sticky cmd.weapon until pmove picked it up (T5 sends cgameUserCmdWeapon in every cmd). */
  private weaponForCmd(ps: PlayerState, now: number): WeaponId {
    const w = this.wantWeapon;
    if (w === WeaponId.NONE) return WeaponId.NONE;
    if ((ps.weaponsOwned & (1 << w)) === 0 || now - this.wantSince > WEAPON_REQUEST_TIMEOUT_MS
      || ps.weaponPending === w || (ps.weapon === w && ps.weaponPending === WeaponId.NONE)) {
      this.wantWeapon = WeaponId.NONE;
      return WeaponId.NONE;
    }
    return w;
  }

  /** Live vertical FOV: ctx.fovY when the app passes the refdef FOV, else derived (CONTRACT-REQUEST #input-1). */
  private resolveFovY(ctx: InputContext): number {
    const s = ctx.settings;
    const hip = Math.abs(s.fov - CG_FOV) < 0.001 ? CG_FOV_VERTICAL : vfovFromCodFov(s.fov);
    if (Math.abs(ctx.fovY - CG_FOV_VERTICAL) > 0.01 || (ctx.adsFrac <= 0 && Math.abs(hip - CG_FOV_VERTICAL) < 0.01)) return ctx.fovY;
    // fallback: linear lerp hip -> weapon adsZoomFov by adsFrac (ESTIMATE; the camera uses adsZoomIn/OutFrac)
    const def = getWeaponDef(ctx.world.predicted.weapon);
    if (!def.aimDownSight || def.adsZoomFov <= 0) return hip;
    const ads = vfovFromCodFov(def.adsZoomFov);
    const a = Math.min(1, Math.max(0, ctx.adsFrac));
    return hip + (ads - hip) * a;
  }

  private visible = (from: readonly number[], to: readonly number[]): boolean => {
    const w = this.world;
    if (!w) return true; // no map collision handed over yet (CONTRACT-REQUEST #input-1)
    w.trace(this.trace, from as Vec3, to as Vec3, this.zero, this.zero, AIM_VIS_MASK);
    return this.trace.fraction >= 1;
  };

  private gatherCandidates(world: ClientWorld): readonly AimCandidate[] {
    const c = this.candidates;
    let n = 0;
    for (const p of world.players) {
      const x = c[n] ?? (c[n] = { clientNum: 0, origin: p.origin, velocity: p.velocity, viewHeight: 0, alive: false });
      x.clientNum = p.clientNum; x.origin = p.origin; x.velocity = p.velocity; x.viewHeight = p.viewHeight; x.alive = p.alive;
      n++;
    }
    c.length = n;
    return c;
  }

  // ---- per render frame -----------------------------------------------------------------------
  frame(ctx: InputContext): InputFrame {
    const now = performance.now();
    const s = ctx.settings;
    this.settings = s;
    const world = ctx.world;
    const ps = world.predicted;
    this.psView = ps;
    const dt = Math.max(0, Math.min(0.1, ctx.dtMs / 1000));
    // respawn / teleport: CG_Respawn -> stand, ADS toggle off; AimAssist_Setup
    if (ps.spawnCount !== this.lastSpawnCount) {
      if (this.lastSpawnCount !== -1) { this.cl.resetForSpawn(); this.aim.reset(); this.wantWeapon = WeaponId.NONE; }
      this.lastSpawnCount = ps.spawnCount;
    }
    if (ps.pm_type !== this.lastPmType) {
      if (this.lastPmType !== -1 && ps.pm_type !== 0 && ps.pm_type !== 1) this.cl.resetForSpawn();
      this.lastPmType = ps.pm_type;
    }
    // menu opened / closed: drop every held key and pending latch (wasPressed taps, stance hold, sprint click)
    // so nothing pressed in the menu or before it leaks into the next cmd (BO2 pause menu).
    const susp = !!ctx.suspended;
    if (susp !== this.suspended) {
      this.suspended = susp;
      this.cl.clearKeys();
      this.mdx = 0; this.mdy = 0;
    }
    if (s.webHidRumble && !this.hidTried) { this.hidTried = true; void this.hid.reconnect(); }
    else if (!s.webHidRumble && this.hid.connected) { this.hidTried = false; void this.hid.close(); } // "reset": stop HID writes

    this.pollPad(now, s);

    // ---- aim assist screen targets from the refdef of the last frame (interpolated players) ----
    const v = world.view;
    this.eye[0] = v.origin[0]; this.eye[1] = v.origin[1]; this.eye[2] = v.origin[2] + v.viewHeight;
    const fovY = this.resolveFovY(ctx);
    const av = this.aimView;
    av.eye = this.eye; av.angles = v.viewangles; av.fovY = fovY; av.cgFov = s.fov;
    av.screenW = this.canvas?.clientWidth || 1280; av.screenH = this.canvas?.clientHeight || 720;
    aimPlayerFromPs(ps, this.aimPlayer);
    this.aim.updateScreenTargets(av, this.aimPlayer, this.gatherCandidates(world), world.localClientNum, this.visible);

    // ---- look ------------------------------------------------------------------------------
    let dPitch = 0, dYaw = 0;
    if (this.padConnected) {
      // CL_GamepadMove: pitch = VA_PITCH * (invert ? 1 : -1); yaw = -VA_YAW
      const pitchAxis = (s.invertPitch ? 1 : -1) * this.va.pitch;
      const yawAxis = -this.va.yaw;
      this.aim.updateGamePad(this.look, {
        pitchAxis, yawAxis, rightAxis: this.va.side, ads: this.cl.adsRequested(), dt,
        sensitivity: sensitivityForSlot(s.lookSensitivity), altCurve: s.altStickCurve,
        targetAssist: s.aimAssist, adsAutoAim: s.adsAutoAim, lockOn: s.aimLockOn && s.aimAssist,
      });
    } else {
      this.aim.updateMouse(this.look, dt);
    }
    dPitch += this.look[0]; dYaw += this.look[1];
    // mouse (CL_MouseMove): m_yaw/m_pitch * counts * sensitivity * zoomSensitivity (tan ratio, T5 CG_CalcFov)
    if (this.mdx || this.mdy) {
      const hipFovY = vfovFromCodFov(s.fov);
      const zoom = Math.tan((fovY * Math.PI) / 360) / Math.tan((hipFovY * Math.PI) / 360);
      const a = Math.min(1, Math.max(0, ps.adsFrac));
      const sens = s.mouseSensitivity * zoom * (1 + (s.mouseAdsScale - 1) * a);
      dYaw -= this.mdx * sens;
      dPitch += this.mdy * sens * (s.invertPitch ? -1 : 1);
      this.mdx = 0; this.mdy = 0;
    }
    // T5 CL_CreateCmd: pitch may not change by more than 90 deg in one frame
    if (dPitch > 90) dPitch = 90; else if (dPitch < -90) dPitch = -90;
    if (!Number.isFinite(dPitch)) dPitch = 0;
    if (!Number.isFinite(dYaw)) dYaw = 0;

    // ---- rumble: event-driven rumble + motor output run in postFrame() (integration fix): this frame's
    // world.frameEvents are only filled by the session AFTER input.frame() returns.

    // ---- debug ------------------------------------------------------------------------------
    const d = this.debug;
    d.yawRate = this.aim.turn.yawSpeed; d.pitchRate = this.aim.turn.pitchSpeed;
    d.slowdown = this.aim.slowdownActive; d.lockOn = this.aim.lockOnTargetEnt; d.targets = this.aim.targetCount;
    d.fovY = fovY; d.device = this.device;

    const pause = this.pauseLatched;
    this.pauseLatched = false;
    return { yawDelta: dYaw, pitchDelta: dPitch, device: this.device, pause, scoreboardHeld: this.cl.isActive(KB.SCORES) };
  }

  /**
   * After session.frame (app.ts): world.frameEvents now holds this frame's pred/ent/game events, so the CTL §7
   * game-event rumbles (pistol_fire, damage_heavy, defaultweapon_melee, grenade_rumble, dtp) can fire; then the
   * mixer drives the motors. (integration fix: these used to run inside frame(), before the events existed.)
   */
  postFrame(ctx: InputContext): void {
    const now = performance.now();
    this.settings = ctx.settings;
    this.rumbleFromEvents(ctx.world, now);
    this.tickRumble(now, ctx.settings);
  }

  /** Scoreboard held (for UI code that does not see InputFrame). */
  get scoreboardHeld(): boolean { return this.cl.isActive(KB.SCORES); }

  // ---- per cmd slot ---------------------------------------------------------------------------
  cmdIntent(ctx: InputContext): CmdIntent {
    const now = performance.now();
    const ps = ctx.world.predicted;
    this.psView = ps;
    const s = ctx.settings;
    this.settings = s;
    this.cl.keyMove(this.km);
    // CL_GamepadMove adds the pad axes on top of the keys (ClampChar of the sum)
    let fwd = clampChar(this.km[0] + (this.padConnected ? this.padForward : 0));
    let right = clampChar(this.km[1] + (this.padConnected ? this.padSide : 0));
    if (fwd < -127) fwd = -127;
    if (right < -127) right = -127;
    const buttons = this.cl.buildButtons(now, this.stanceView(), fwd);
    const weapon = this.weaponForCmd(ps, now);
    // auto-melee lunge target on the melee press edge (pad and mouse alike)
    const meleeDown = (buttons & Button.MELEE) !== 0;
    this.aim.autoMeleeOnCmd(meleeDown, this.prevMelee, this.melee);
    this.prevMelee = meleeDown;
    const meleeChargeDist = this.melee.dist;
    const meleeChargeYaw = meleeChargeDist > 0 ? angleToShort(this.melee.yaw) : 0;
    if (meleeChargeDist > 0) this.debug.autoMelee = meleeChargeDist;
    this.debug.lastButtons = buttons;
    return { buttons, forwardmove: fwd, rightmove: right, weapon, meleeChargeYaw, meleeChargeDist };
  }

  // ---- rumble ---------------------------------------------------------------------------------
  private rumbleFromEvents(world: ClientWorld, now: number): void {
    const me = world.localClientNum;
    for (const e of world.frameEvents) {
      if (e.src === 'pred') {
        if (e.ev === PEv.WEAPON_FIRE || e.ev === PEv.WEAPON_FIRE_LAST) {
          const r = getWeaponDef((e.parm & 0xff) as WeaponId).raw['fireRumble']; // WPN fireRumble (pistol_fire)
          if (typeof r === 'string' && r) this.mixer.play(r, now);
        } else if (e.ev === PEv.DIVE_LAND) {
          this.mixer.play('dtp_rumble', now); // ESTIMATE (engine) [CTL §7]
        }
      } else if (e.src === 'game') {
        const g = e.ge;
        if (g.type === GameEventType.DAMAGE_TAKEN) {
          this.mixer.play('damage_heavy', now); // _globallogic_player.gsc damageshellshockandrumble
        } else if (g.type === GameEventType.MELEE_HIT && g.attacker === me) {
          const r = getWeaponDef(WeaponId.KNIFE).raw['meleeImpactRumble']; // knife_mp defaultweapon_melee
          this.mixer.play(typeof r === 'string' && r ? r : 'defaultweapon_melee', now);
        } else if (g.type === GameEventType.EXPLOSION) {
          const o = world.view.origin;
          const dx = g.pos[0] - o[0], dy = g.pos[1] - o[1], dz = g.pos[2] - o[2];
          this.mixer.playAt('grenade_rumble', now, Math.sqrt(dx * dx + dy * dy + dz * dz)); // explosionRumble, range 400
        }
      }
    }
  }

  private activeActuator(): HapticActuatorLike | null {
    if (this.padIndex < 0 || typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
    const gp = navigator.getGamepads()[this.padIndex] as (Gamepad & { vibrationActuator?: HapticActuatorLike }) | null;
    const a = gp?.vibrationActuator;
    if (!a || typeof a.playEffect !== 'function') return null;
    if (a.effects && !a.effects.includes('dual-rumble')) return null;
    return a;
  }

  private tickRumble(now: number, s: Settings): void {
    if (now - this.lastRumbleSend < RUMBLE_INTERVAL_MS) return;
    this.lastRumbleSend = now;
    if (!s.rumble) { if (this.rumbleWasOn) this.stopRumble(); this.mixer.stopAll(); return; } // gpad_rumble
    this.mixer.sample(now, this.motors);
    const on = this.motors.strong > 0.001 || this.motors.weak > 0.001;
    if (!on && !this.rumbleWasOn) return;
    this.rumbleWasOn = on;
    if (this.hid.connected) { this.hid.send(this.motors.strong, this.motors.weak); return; }
    const act = this.activeActuator();
    if (!act) return;
    if (!on) { void act.reset?.()?.catch?.(() => undefined); return; }
    void act.playEffect!('dual-rumble', {
      duration: RUMBLE_EFFECT_MS, startDelay: 0, strongMagnitude: this.motors.strong, weakMagnitude: this.motors.weak,
    })?.catch?.(() => undefined);
  }

  private stopRumble(): void {
    this.mixer.stopAll();
    this.rumbleWasOn = false;
    if (this.hid.connected) this.hid.send(0, 0);
    const act = this.activeActuator();
    void act?.reset?.()?.catch?.(() => undefined);
  }
}

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}
